'use client';
import useGeolocation from '@/hooks/useGeolocation';
import { buildMarkerClustererElement } from '@/services/map';
import {
  Marker,
  MarkerClusterer,
  SuperClusterAlgorithm
} from '@googlemaps/markerclusterer';
import { useMap as useMapPrimitive } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useMemo, useState } from 'react';

export default function useMap() {
  const map = useMapPrimitive('EVENTS_MAP');
  const { loading: geolocationLoading, location } = useGeolocation();
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const clusterer = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({
        radius: 60,
        maxZoom: 20
      }),
      renderer: {
        render: ({ position, count }) => {
          const clusterElement = buildMarkerClustererElement(count);
          const markerElement = new google.maps.marker.AdvancedMarkerElement({
            position,
            content: clusterElement
          });
          return markerElement;
        }
      },
      onClusterClick: (_, cluster, map) => {
        const bounds = cluster.bounds;
        if (bounds) {
          map.fitBounds(bounds);
        }
      }
    });
  }, [map]);

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  const setMarkerRef = useCallback(
    (marker: Marker | null, key: string) => {
      if ((marker && markers[key]) || (!marker && !markers[key])) return null;
      setMarkers((markers) => {
        if (marker) {
          return { ...markers, [key]: marker };
        } else {
          const { [key]: _, ...newMarkers } = markers;

          return newMarkers;
        }
      });
    },
    [markers]
  );

  return {
    mapPrimitive: map,
    setMarkerRef,
    geolocationLoading,
    location
  };
}
